import { AvailableTransferMaterial } from "../../../../models/materialStockTransfer.model";
import {
  convertStockQuantity,
  getDefaultUnitForType,
} from "../../../../utils/warehouse.util";

const UNIT_TYPE_BY_UNIT: Record<string, string> = {
  mg: "weight",
  g: "weight",
  kg: "weight",
  ml: "liquid",
  l: "liquid",
  mm: "length",
  cm: "length",
  m: "length",
  piece: "piece",
};

export const getMaterialDisplayUnit = (baseUnit: string): string => {
  const unitType = UNIT_TYPE_BY_UNIT[baseUnit.toLowerCase()];
  return unitType ? getDefaultUnitForType(unitType) : baseUnit;
};

export const convertMaterialQuantity = (
  quantity: number,
  fromUnit: string,
  toUnit: string,
): number | null => {
  const normalizedFromUnit = fromUnit.toLowerCase();
  const normalizedToUnit = toUnit.toLowerCase();
  if (normalizedFromUnit === normalizedToUnit) return quantity;

  const fromUnitType = UNIT_TYPE_BY_UNIT[normalizedFromUnit];
  const toUnitType = UNIT_TYPE_BY_UNIT[normalizedToUnit];
  if (!fromUnitType || fromUnitType !== toUnitType) return null;

  return convertStockQuantity(quantity, fromUnit, toUnit, fromUnitType);
};

export const parseSerialNumbers = (value: string): string[] =>
  value
    .split(/[\n,;]+/)
    .map((serial) => serial.trim())
    .filter(Boolean);

export interface MaterialTransferValidationResult {
  error?: string;
  quantity?: number;
  serialNumbers?: string[];
}

export const validateMaterialTransferQuantity = (
  material: AvailableTransferMaterial,
  quantityInput: string,
  serialNumbersInput: string,
): MaterialTransferValidationResult => {
  const displayQuantity = Number(quantityInput);
  if (!Number.isFinite(displayQuantity) || displayQuantity <= 0) {
    return { error: "Quantity must be greater than zero" };
  }

  const displayUnit = getMaterialDisplayUnit(material.unit);
  const quantity = convertMaterialQuantity(
    displayQuantity,
    displayUnit,
    material.unit,
  );
  if (quantity === null) {
    return { error: `Cannot convert ${displayUnit} to ${material.unit}` };
  }
  if (quantity > material.quantity + 0.000001) {
    return { error: "Quantity exceeds available stock" };
  }

  if (!material.requireSerialNumber) return { quantity };

  const serialNumbers = parseSerialNumbers(serialNumbersInput);
  const uniqueSerialNumbers = new Set(serialNumbers);
  if (uniqueSerialNumbers.size !== serialNumbers.length) {
    return { error: "Serial numbers must be unique" };
  }
  if (!Number.isInteger(quantity) || serialNumbers.length !== quantity) {
    return {
      error: "Quantity must be a positive integer equal to the serial count",
    };
  }

  return { quantity, serialNumbers };
};
